import React from 'react';
import { Building2, MapPin, FileText, Plus, Pencil, Trash2 } from 'lucide-react';
import { CodeNAFAutocomplete } from '../CodeNAFAutocomplete';
import TranchesEligibilite from '../TranchesEligibilite';

/** Étape 1 : choix mono / multi. */
export const Step1Type = ({ ctx }) => {
  const { handleTypeSelection } = ctx;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <button
        onClick={() => handleTypeSelection('mono')}
        className="group p-8 bg-white border-2 border-[#BFC6CC] hover:border-[#397DFF] rounded-xl transition-all hover:shadow-lg"
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 bg-[#397DFF]/10 rounded-full flex items-center justify-center group-hover:bg-[#397DFF]/20 transition-colors">
            <MapPin className="w-8 h-8 text-[#397DFF]" />
          </div>
          <div>
            <h3 className="text-xl font-heading font-semibold text-[#191D1E] mb-2">Rapport Mono-site</h3>
            <p className="text-sm text-[#687175]">Analyse pour un seul Point De Livraison (PDL)</p>
          </div>
        </div>
      </button>

      <button
        onClick={() => handleTypeSelection('multi')}
        className="group p-8 bg-white border-2 border-[#BFC6CC] hover:border-[#397DFF] rounded-xl transition-all hover:shadow-lg"
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 bg-[#397DFF]/10 rounded-full flex items-center justify-center group-hover:bg-[#397DFF]/20 transition-colors">
            <Building2 className="w-8 h-8 text-[#397DFF]" />
          </div>
          <div>
            <h3 className="text-xl font-heading font-semibold text-[#191D1E] mb-2">Rapport Multi-site</h3>
            <p className="text-sm text-[#687175]">Consolidation de plusieurs PDL pour un même client</p>
          </div>
        </div>
      </button>
    </div>
  );
};

/** Étape 2 : Mode calcul SIREN vs SIRET (multi-site uniquement). */
export const Step2ModeCalcul = ({ ctx }) => {
  const { setStep, handleModeSelection } = ctx;
  return (
    <div>
      <div className="mb-6">
        <button onClick={() => setStep(1)} className="text-[#687175] hover:text-[#191D1E] text-sm">
          ← Retour au choix du type
        </button>
      </div>
      <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">Mode de Calcul Multi-site</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => handleModeSelection('siren')}
          className="group p-8 bg-white border-2 border-[#BFC6CC] hover:border-[#397DFF] rounded-xl transition-all hover:shadow-lg text-left"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#397DFF]/10 rounded-full flex items-center justify-center group-hover:bg-[#397DFF]/20 transition-colors flex-shrink-0">
              <Building2 className="w-6 h-6 text-[#397DFF]" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-[#191D1E] mb-2">Ratio Regroupé (SIREN)</h3>
              <p className="text-sm text-[#687175] mb-3">Une seule Valeur Ajoutée pour toute l'entreprise</p>
              <ul className="text-xs text-[#687175] space-y-1">
                <li>✓ Consommations agrégées de tous les sites</li>
                <li>✓ Un ratio global TICFE/VAC</li>
                <li>✓ Une éligibilité commune</li>
                <li>✓ Plus simple à gérer</li>
              </ul>
            </div>
          </div>
        </button>

        <button
          onClick={() => handleModeSelection('siret')}
          className="group p-8 bg-white border-2 border-[#BFC6CC] hover:border-[#397DFF] rounded-xl transition-all hover:shadow-lg text-left"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#F9C80E]/10 rounded-full flex items-center justify-center group-hover:bg-[#F9C80E]/20 transition-colors flex-shrink-0">
              <FileText className="w-6 h-6 text-[#F9C80E]" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-[#191D1E] mb-2">Ratio Site par Site (SIRET)</h3>
              <p className="text-sm text-[#687175] mb-3">Valeur Ajoutée propre à chaque site</p>
              <ul className="text-xs text-[#687175] space-y-1">
                <li>✓ VAC individuelle par site</li>
                <li>✓ Ratio calculé par site</li>
                <li>✓ Éligibilité propre à chaque site</li>
                <li>✓ Analyse détaillée</li>
              </ul>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

/** Étape 3 : Informations entreprise + PDLs (mono) ou recap (multi). */
export const Step3InfosEntreprise = ({ ctx }) => {
  const {
    rapportType, modeCalcul, formData, setFormData,
    currentPDL, setCurrentPDL, editingPDLIndex, setEditingPDLIndex,
    agences, commerciauxFiltres,
    setStep, sauvegarderBrouillon,
  } = ctx;

  return (
    <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
      <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
        {rapportType === 'mono' ? 'Informations Client' : 'Informations Entreprise'}
      </h2>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">SIREN *</label>
            <input
              type="text" required pattern="[0-9]{9}" maxLength="9"
              value={formData.siren}
              onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
              className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] font-mono"
              placeholder="9 chiffres"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Nom Entreprise *</label>
            <input
              type="text" required
              value={formData.nom_entreprise}
              onChange={(e) => setFormData({ ...formData, nom_entreprise: e.target.value })}
              className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
              placeholder="Raison sociale"
            />
          </div>

          {rapportType === 'mono' && (
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-[#191D1E]">Points De Livraison (PDL) *</label>
                <button
                  type="button"
                  onClick={() => {
                    if (!currentPDL.pdl || !currentPDL.puissance_souscrite || !currentPDL.code_naf) {
                      alert('Remplissez tous les champs du PDL');
                      return;
                    }
                    if (editingPDLIndex !== null) {
                      const newPdls = [...formData.pdls];
                      newPdls[editingPDLIndex] = { ...currentPDL };
                      setFormData({ ...formData, pdls: newPdls });
                      setEditingPDLIndex(null);
                    } else {
                      setFormData({ ...formData, pdls: [...formData.pdls, { ...currentPDL }] });
                    }
                    setCurrentPDL({ pdl: '', puissance_souscrite: '', code_naf: '', tranches_eligibilite: [] });
                  }}
                  className="flex items-center gap-2 text-sm bg-[#397DFF] hover:bg-[#4672B6] text-white px-4 py-2 rounded-lg"
                >
                  {editingPDLIndex !== null ? <><Pencil className="w-4 h-4" /> Valider la modification</> : <><Plus className="w-4 h-4" /> Ajouter PDL</>}
                </button>
                {editingPDLIndex !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPDLIndex(null);
                      setCurrentPDL({ pdl: '', puissance_souscrite: '', code_naf: '', tranches_eligibilite: [] });
                    }}
                    className="text-sm px-4 py-2 border border-[#BFC6CC] text-[#687175] rounded-lg hover:bg-[#F5F6F9]"
                  >Annuler</button>
                )}
              </div>

              {formData.pdls.length > 0 && (
                <div className="mb-4 space-y-2">
                  {formData.pdls.map((pdl, index) => (
                    <div key={index} className={`flex items-center gap-3 p-3 border rounded-lg ${editingPDLIndex === index ? 'bg-[#E8F4FD] border-[#397DFF]' : 'bg-[#F5F6F9] border-[#BFC6CC]'}`}>
                      <div className="flex-1">
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-[#687175]">PDL:</span> <span className="font-mono text-[#191D1E]">{pdl.pdl}</span></div>
                          <div><span className="text-[#687175]">Puissance:</span> <span className="text-[#191D1E]">{pdl.puissance_souscrite} kVA</span></div>
                          <div><span className="text-[#687175]">NAF:</span> <span className="font-mono text-[#191D1E]">{pdl.code_naf}</span></div>
                        </div>
                        {pdl.tranches_eligibilite && pdl.tranches_eligibilite.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {pdl.tranches_eligibilite.map((t, ti) => (
                              <span key={ti} className={`inline-block text-xs px-1.5 py-0.5 rounded ${t.motif === 'EXONERATION' ? 'bg-emerald-100 text-emerald-700' : t.motif === 'FUITE_CARBONE' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                {t.part}% {t.motif === 'EXONERATION' ? 'exo' : t.motif === 'FUITE_CARBONE' ? 'fuite C' : 'élec-int'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => { setCurrentPDL({ ...pdl }); setEditingPDLIndex(index); }}
                        className="text-[#397DFF] hover:text-[#4672B6]"
                        title="Modifier ce PDL"
                        data-testid={`ticfe-edit-pdl-${index}`}
                      ><Pencil className="w-4 h-4" /></button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, pdls: formData.pdls.filter((_, i) => i !== index) })}
                        className="text-red-600 hover:text-red-700"
                      ><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 p-4 bg-white border-2 border-dashed border-[#BFC6CC] rounded-lg">
                <div>
                  <input
                    type="text" value={currentPDL.pdl}
                    onChange={(e) => setCurrentPDL({ ...currentPDL, pdl: e.target.value })}
                    className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] font-mono text-sm"
                    placeholder="N° PDL"
                  />
                </div>
                <div>
                  <input
                    type="number" step="0.01" value={currentPDL.puissance_souscrite}
                    onChange={(e) => setCurrentPDL({ ...currentPDL, puissance_souscrite: e.target.value })}
                    className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] text-sm"
                    placeholder="Puissance kVA"
                  />
                </div>
                <div>
                  <CodeNAFAutocomplete
                    value={currentPDL.code_naf}
                    onChange={(val) => setCurrentPDL({ ...currentPDL, code_naf: val })}
                  />
                </div>
              </div>

              {currentPDL.code_naf && (
                <div className="mt-3 ml-1 p-3 bg-white border border-dashed border-[#BFC6CC] rounded-lg">
                  <label className="block text-xs font-medium text-[#687175] mb-1">Tranches d'éligibilité</label>
                  <TranchesEligibilite
                    pdl={currentPDL.pdl}
                    codeNaf={currentPDL.code_naf}
                    tranches={currentPDL.tranches_eligibilite || []}
                    onChange={(t) => setCurrentPDL({ ...currentPDL, tranches_eligibilite: t })}
                  />
                </div>
              )}

              {formData.pdls.length === 0 && (
                <p className="text-xs text-[#687175] mt-2">ℹ️ Ajoutez au moins un PDL. Vous pouvez en ajouter plusieurs pour le même site.</p>
              )}
            </div>
          )}
        </div>

        {modeCalcul === 'siren' && (
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Valeur Ajoutée Comptable (€)</label>
            <input
              type="number" step="0.01"
              value={formData.valeur_ajoutee_siren}
              onChange={(e) => setFormData({ ...formData, valeur_ajoutee_siren: e.target.value })}
              className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] font-mono"
              placeholder="VAC de l'entreprise (optionnel)"
              data-testid="vac-siren-input"
            />
            <p className="text-xs text-[#687175] mt-1">
              Optionnel : laisser vide si l'éligibilité repose sur une exonération par code NAF.
              Sinon, valeur commune pour tous les sites (mode regroupé SIREN).
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Début Période Fiscale *</label>
            <input
              type="date" required
              value={formData.debut_periode}
              onChange={(e) => setFormData({ ...formData, debut_periode: e.target.value })}
              className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Fin Période Fiscale *</label>
            <input
              type="date" required
              value={formData.fin_periode}
              onChange={(e) => setFormData({ ...formData, fin_periode: e.target.value })}
              className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 pt-6 border-t border-[#BFC6CC]">
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Agence PDE *</label>
            <select
              required value={formData.agence_id}
              onChange={(e) => setFormData({ ...formData, agence_id: e.target.value, commercial_id: '' })}
              className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
            >
              <option value="">Sélectionner une agence</option>
              {agences.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#191D1E] mb-2">Commercial en charge *</label>
            <select
              required disabled={!formData.agence_id} value={formData.commercial_id}
              onChange={(e) => setFormData({ ...formData, commercial_id: e.target.value })}
              className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] disabled:bg-[#F5F6F9]"
            >
              <option value="">Sélectionner un commercial</option>
              {commerciauxFiltres.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <span className="text-amber-500 text-xl">📝</span>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-amber-800 mb-2">Remarques / Commentaires pour cette analyse</label>
              <textarea
                value={formData.commentaire_global || ''}
                onChange={(e) => setFormData({ ...formData, commentaire_global: e.target.value })}
                placeholder="Ajoutez ici vos remarques, observations ou points de clarification pour cette analyse TICFE..."
                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-h-[60px] resize-y"
                rows={2}
              />
              <p className="text-xs text-amber-600 mt-1">Ce commentaire sera inclus dans le rapport final.</p>
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-3 pt-6">
          <button
            onClick={() => setStep(rapportType === 'multi' ? 2 : 1)}
            className="px-6 py-3 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9]"
          >← Retour</button>
          <div className="flex gap-3">
            <button
              type="button" onClick={sauvegarderBrouillon}
              className="px-6 py-3 border border-[#F9C80E] text-[#F9C80E] font-medium rounded-lg hover:bg-[#FFF9E6]"
            >💾 Enregistrer Brouillon</button>
            <button
              onClick={() => setStep(rapportType === 'mono' ? 5 : 4)}
              className="bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-3 px-8 rounded-lg"
            >{rapportType === 'mono' ? 'Suivant : Consommations →' : 'Suivant : Sites et PDL →'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};
